import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import { Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import type { DropdownItem } from "../../../../../components/UIToolkit/Inputs/SystemDropDown/types";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { CONTROL_HEIGHT, ICON_SIZE, INK } from "../../../../../theme/tokens";
import type { Job } from "../../../../../utils/models";
import { RemoveJobConfirmDialog } from "./RemoveJobConfirmDialog";
import type { JobsEditState } from "./useJobsEditState";

type Props = {
	availableJobs: Job[];
	selectedJobIds: string[];
	onJobsChange: (jobIds: string[]) => void;
	showJobRecommendation: boolean;
	recommendedJobs: Job[];
	onApplyRecommendedJobs: () => void;
	jobsEdit: JobsEditState;
	employeeName: string;
	/** The field column's width, shared with its sibling fields. */
	fieldWidth: string;
};

/**
 * The Job field in edit mode: the job picker and the remove-job confirmation.
 * Picked rows show a read-only bell because the row already has a checkbox. The switch lives in the table.
 */
export const JobFieldEdit: FC<Props> = ({
	availableJobs,
	selectedJobIds,
	onJobsChange,
	showJobRecommendation,
	recommendedJobs,
	onApplyRecommendedJobs,
	jobsEdit,
	employeeName,
	fieldWidth,
}) => {
	const { t } = useTranslation();
	const { rows, pendingRemoval, cancelRemove, confirmRemove, setPrimaryAndRemove, muteInstead, onDropdownOpen, onDropdownClose } = jobsEdit;

	const items = useMemo<DropdownItem<string>[]>(() => {
		const silentById = new Map(rows.map((row) => [row.id, row.isSilent]));
		return availableJobs.map((job) => {
			const isSilent = silentById.get(job.id);
			return {
				value: job.id,
				label: job.displayName,
				selectedLabel: job.displayName,
				endAdornment:
					isSilent === undefined ? undefined : (
						<Box
							component="span"
							aria-hidden
							sx={{ display: "inline-flex", color: isSilent ? INK.DISABLED : INK.MUTED, "& > svg": { fontSize: ICON_SIZE.PILL } }}
						>
							{isSilent ? <NotificationsOffOutlinedIcon /> : <NotificationsNoneOutlinedIcon />}
						</Box>
					),
			};
		});
	}, [availableJobs, rows]);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
			{showJobRecommendation && (
				<InlineHint
					dataTestId="profile-job-recommendation"
					sx={{ maxWidth: fieldWidth }}
					actionPlacement="below"
					action={
						<SystemButton
							buttonVariant="borderless"
							size="small"
							noFullWidthMobile
							data-testid="profile-job-recommendation-apply"
							onClick={onApplyRecommendedJobs}
						>
							{t("profile.jobs.recommendation.apply")}
						</SystemButton>
					}
				>
					{t("profile.jobs.recommendation", { jobs: recommendedJobs.map((job) => job.displayName).join(", ") })}
				</InlineHint>
			)}
			<SystemDropdown
				formControlSx={{ width: fieldWidth }}
				selectSx={{ minHeight: CONTROL_HEIGHT.STANDARD }}
				data-testid="profile-jobs-dropdown"
				multiple
				fullWidth
				minimalSelection={1}
				fieldLabel={t("job")}
				items={items}
				selectedItems={selectedJobIds}
				onChangeMultiple={onJobsChange}
				onOpen={onDropdownOpen}
				onClose={onDropdownClose}
			/>
			{pendingRemoval && (
				<RemoveJobConfirmDialog
					job={pendingRemoval}
					otherJobs={rows.filter((row) => row.id !== pendingRemoval.id)}
					employeeName={employeeName}
					onCancel={() => cancelRemove(pendingRemoval.id)}
					onRemove={() => confirmRemove(pendingRemoval.id)}
					onMuteInstead={() => muteInstead(pendingRemoval.id)}
					onSetPrimaryAndRemove={(newPrimaryId) => setPrimaryAndRemove(newPrimaryId, pendingRemoval.id)}
				/>
			)}
		</Box>
	);
};
